// src/components/WhyTrustUs.js

import React from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'

export default function WhyTrustUs() {
  const items = [
    {
      icon: '/assets/shield.png',
      title: 'Verified Vendors',
      desc: 'Every vendor is manually reviewed and quality-checked before going live.',
      gradient: 'from-emerald-500 to-teal-500',
      bgGradient: 'from-emerald-50 to-teal-50',
      borderColor: 'border-emerald-200'
    },
    {
      icon: '/assets/diya1.png',
      title: 'Built for Indian Traditions',
      desc: 'From pujas to weddings, we support cultural needs across all event types.',
      gradient: 'from-orange-500 to-amber-500',
      bgGradient: 'from-orange-50 to-amber-50',
      borderColor: 'border-orange-200'
    },
    {
      icon: '/assets/pin.png',
      title: 'Local, U.S.-Based Listings',
      desc: 'Find nearby vendors who understand desi traditions with U.S. availability.',
      gradient: 'from-blue-500 to-indigo-500',
      bgGradient: 'from-blue-50 to-indigo-50',
      borderColor: 'border-blue-200'
    },
    {
      icon: '/assets/people.png',
      title: 'Created by the Community',
      desc: 'Founded by Indian families in the U.S. to help others plan stress-free events.',
      gradient: 'from-purple-500 to-pink-500',
      bgGradient: 'from-purple-50 to-pink-50',
      borderColor: 'border-purple-200'
    },
  ]

  return (
    <section
      id="why-trust-us"
      className="relative bg-gradient-to-br from-slate-50 via-orange-50/30 to-white py-24 px-6 md:px-20 overflow-hidden"
    >
      {/* Background decorative elements */}
      <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] bg-gradient-to-r from-orange-100/50 to-amber-100/50 rounded-full blur-3xl opacity-30 -z-10"></div>
      <div className="absolute bottom-1/3 left-1/5 w-[300px] h-[300px] bg-gradient-to-r from-blue-100/40 to-indigo-100/40 rounded-full blur-3xl opacity-25 -z-10"></div>
      
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-5 -z-10" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23f97316' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3Ccircle cx='10' cy='10' r='1'/%3E%3Ccircle cx='50' cy='10' r='1'/%3E%3Ccircle cx='10' cy='50' r='1'/%3E%3Ccircle cx='50' cy='50' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }}></div>

      <div className="max-w-7xl mx-auto">
        {/* Enhanced header section */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-orange-100 to-amber-100 rounded-full text-sm font-medium text-orange-700 mb-6 shadow-sm">
            <svg className="w-4 h-4 mr-2 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Trusted by 1000+ Families
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="text-slate-800">Why Indian Families</span>
            <br />
            <span className="bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">Trust Us</span>
          </h2>
          
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Built by the community, for the community. We understand your cultural needs and deliver authentic experiences.
          </p>
        </div>

        {/* Enhanced cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {items.map(({ icon, title, desc, gradient, bgGradient, borderColor }, index) => (
            <div
              key={title}
              className="group relative"
            >
              {/* Card with enhanced styling */}
              <div className={`
                relative
                transform 
                transition-all 
                duration-500 
                hover:-translate-y-3 
                hover:shadow-2xl 
                bg-white/90 
                backdrop-blur-sm 
                p-8 
                rounded-3xl 
                shadow-lg 
                border 
                border-white/50
                hover:border-white/80
                overflow-hidden
                group-hover:bg-white
              `}>
                {/* Gradient overlay on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${bgGradient} opacity-0 group-hover:opacity-30 transition-opacity duration-500 rounded-3xl`}></div>
                
                {/* Content */}
                <div className="relative z-10">
                  {/* Icon and title section */}
                  <div className="flex items-start space-x-4 mb-6">
                    {/* Enhanced icon container */}
                    <div className="relative">
                      <div className={`absolute inset-0 bg-gradient-to-r ${gradient} rounded-2xl blur-lg opacity-20 group-hover:opacity-40 transition-opacity duration-500`}></div>
                      <div className={`relative bg-gradient-to-br ${bgGradient} p-4 rounded-2xl border ${borderColor} group-hover:scale-110 transition-transform duration-300 shadow-sm`}>
                        <img 
                          src={icon} 
                          alt={title} 
                          className="h-8 w-8 flex-shrink-0 drop-shadow-sm group-hover:drop-shadow-md transition-all duration-300" 
                        />
                      </div>
                    </div>
                    
                    {/* Title */}
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-slate-800 group-hover:text-slate-900 transition-colors duration-300 mb-1">
                        {title}
                      </h3>
                      <div className={`h-1 w-12 bg-gradient-to-r ${gradient} rounded-full opacity-60 group-hover:opacity-100 group-hover:w-16 transition-all duration-500`}></div>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-slate-600 leading-relaxed text-lg group-hover:text-slate-700 transition-colors duration-300">
                    {desc}
                  </p>

                  {/* Hover indicator */}
                  <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className={`w-3 h-3 bg-gradient-to-r ${gradient} rounded-full animate-pulse`}></div>
                  </div>
                </div>

                {/* Floating accent elements */}
                <div className={`absolute -top-2 -right-2 w-4 h-4 bg-gradient-to-r ${gradient} rounded-full opacity-20 group-hover:opacity-40 transition-opacity duration-500`}></div>
                <div className={`absolute -bottom-2 -left-2 w-6 h-6 bg-gradient-to-r ${gradient} rounded-full opacity-10 group-hover:opacity-30 transition-opacity duration-500`}></div>
              </div>

              {/* Card number indicator */}
              <div className="absolute -top-3 -left-3 w-8 h-8 bg-white border-2 border-slate-200 rounded-full flex items-center justify-center text-sm font-bold text-slate-600 shadow-lg group-hover:border-orange-300 group-hover:text-orange-600 transition-colors duration-300">
                {index + 1}
              </div>
            </div>
          ))}
        </div>

        {/* Enhanced trust indicators section */}
        <div className="text-center">
          <div className="inline-flex flex-wrap items-center justify-center gap-8 bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl px-8 py-6 shadow-lg">
            <div className="flex items-center space-x-2 text-slate-600">
              <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <div className="font-semibold text-slate-800">100% Verified</div>
                <div className="text-sm text-slate-500">Background checked</div>
              </div>
            </div>
            
            <div className="h-12 w-px bg-slate-300"></div>
            
            <div className="flex items-center space-x-2 text-slate-600">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <div className="font-semibold text-slate-800">24/7 Support</div>
                <div className="text-sm text-slate-500">Always here to help</div>
              </div>
            </div>
            
            <div className="h-12 w-px bg-slate-300"></div>
            
            <div className="flex items-center space-x-2 text-slate-600">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <div className="font-semibold text-slate-800">Community Built</div>
                <div className="text-sm text-slate-500">By families, for families</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced scroll-down indicator */}
      <a
        href="#testimonials"
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 group"
        aria-label="Scroll to testimonials"
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-sm text-slate-500 font-medium group-hover:text-slate-700 transition-colors duration-200 opacity-0 group-hover:opacity-100">
            Read what families say
          </span>
          <div className="p-3 bg-white/80 backdrop-blur-sm rounded-full shadow-lg border border-white/50 group-hover:shadow-xl transition-all duration-200 group-hover:bg-orange-50/80">
            <ChevronDownIcon className="h-6 w-6 text-slate-600 animate-bounce group-hover:text-orange-600 transition-colors duration-200" />
          </div>
        </div>
      </a>
    </section>
  )
}